import { VeevaVaultAssetImportStatus, VeevaVaultFileImportStatus } from '@atlas/types'
import { Badge, type BadgeProps, Button, cn } from '@atlas/ui-primitives'
import { ChevronRight, Download, File, FileImage, FileText, FileVideo, LoaderCircle } from 'lucide-react'
import { useEffect, useId, useRef } from 'react'
import { useVeevaVaultFileDownload } from '@/hooks/useVeevaVault'
import { notification } from '@/notifications/notification-store'
import type { VeevaVaultAssetImport } from '@/services/veeva-vault.service'
import { downloadErrorMessage } from './errors'

type Status = VeevaVaultAssetImportStatus | VeevaVaultFileImportStatus
type AssetFile = VeevaVaultAssetImport['files'][number]

const STATUS_BADGES: Record<Status, { label: string; variant: BadgeProps['variant'] }> = {
	[VeevaVaultAssetImportStatus.IN_PROGRESS]: { label: 'In progress', variant: 'secondary' },
	[VeevaVaultAssetImportStatus.COMPLETED]: { label: 'Completed', variant: 'success' },
	[VeevaVaultAssetImportStatus.FAILED]: { label: 'Failed', variant: 'danger' },
	[VeevaVaultFileImportStatus.PENDING]: { label: 'Pending', variant: 'muted' },
	[VeevaVaultFileImportStatus.DOWNLOADING]: { label: 'Downloading', variant: 'secondary' },
	[VeevaVaultFileImportStatus.SUCCESS]: { label: 'Imported', variant: 'success' },
	[VeevaVaultFileImportStatus.FAILURE]: { label: 'Failed', variant: 'danger' },
}

const isWorking = (status: Status) =>
	status === VeevaVaultAssetImportStatus.IN_PROGRESS || status === VeevaVaultFileImportStatus.DOWNLOADING

const StatusBadge = ({ status }: { status: Status }) => (
	<Badge size="sm" variant={STATUS_BADGES[status].variant} className="gap-ds-1">
		{isWorking(status) && <LoaderCircle aria-hidden className="h-3 w-3 motion-safe:animate-spin" />}
		{STATUS_BADGES[status].label}
	</Badge>
)

const SEGMENT_COLORS: Record<VeevaVaultFileImportStatus, string> = {
	[VeevaVaultFileImportStatus.PENDING]: 'bg-ds-border',
	[VeevaVaultFileImportStatus.DOWNLOADING]: 'bg-ds-primary motion-safe:animate-pulse',
	[VeevaVaultFileImportStatus.SUCCESS]: 'bg-ds-success',
	[VeevaVaultFileImportStatus.FAILURE]: 'bg-ds-danger',
}

/** One segment per file, so an import's progress and its failures read at a glance without opening it. */
function FileStrip({ files, imported }: { files: AssetFile[]; imported: number }) {
	return (
		<span className="flex w-full flex-col gap-ds-1">
			<span role="img" aria-label={`${imported} of ${files.length} files imported`} className="flex h-1.5 gap-0.5">
				{files.map((f) => (
					<span key={f.id} className={cn('flex-1 rounded-ds-full transition-colors', SEGMENT_COLORS[f.status])} />
				))}
			</span>
			<span className="text-ds-xs text-ds-text-muted tabular-nums">
				{imported} / {files.length} files
			</span>
		</span>
	)
}

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
	['day', 86_400],
	['hour', 3_600],
	['minute', 60],
]

const fromNow = (iso: string) => {
	const seconds = (new Date(iso).getTime() - Date.now()) / 1000
	const unit = UNITS.find(([, size]) => Math.abs(seconds) >= size)
	return unit ? RELATIVE.format(Math.round(seconds / unit[1]), unit[0]) : 'just now'
}

const formatDateTime = (iso: string) =>
	new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })

/** `source` and Vault's `<name>_rendition__v` codes, as a person would name them. */
const fileLabel = (file: string) => {
	if (file === 'source') return 'Source file'
	const words = file.replace(/__[a-z]+$/, '').replaceAll('_', ' ')
	return words.charAt(0).toUpperCase() + words.slice(1)
}

const FileGlyph = ({ format }: { format: string | null }) => {
	const Glyph = format?.startsWith('image/')
		? FileImage
		: format?.startsWith('video/')
			? FileVideo
			: format?.includes('pdf') || format?.startsWith('text/')
				? FileText
				: File
	return <Glyph aria-hidden className="h-5 w-5" />
}

/** `application/pdf` → `PDF`; a vendor MIME keeps only its last token. */
const formatLabel = (format: string | null) => format?.split(/[/.+]/).pop()?.toUpperCase() ?? 'Unknown format'

interface ImportRowProps {
	assetImport: VeevaVaultAssetImport
	isOpen: boolean
	isFresh: boolean
	onToggle: () => void
}

/** One imported asset code: a summary line that expands in place to its files and their downloads. */
export function ImportRow({ assetImport, isOpen, isFresh, onToggle }: ImportRowProps) {
	const panelId = useId()
	const ref = useRef<HTMLLIElement>(null)
	useEffect(() => {
		if (isFresh) ref.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
	}, [isFresh])
	const { files, status } = assetImport
	const [first] = files
	const imported = files.filter((f) => f.status === VeevaVaultFileImportStatus.SUCCESS)
	const inLibrary = imported.flatMap((f) => (f.fileId ? [{ file: f, fileId: f.fileId }] : []))

	return (
		<li
			ref={ref}
			className={cn(
				'overflow-hidden rounded-ds-lg border bg-ds-background transition-shadow',
				isOpen ? 'border-ds-border-strong shadow-ds-md' : 'border-ds-border hover:shadow-ds-sm',
				isFresh && 'animate-ds-deal-in',
			)}
		>
			<button
				type="button"
				aria-expanded={isOpen}
				aria-controls={panelId}
				onClick={onToggle}
				className="grid w-full cursor-pointer grid-cols-[auto_1fr_auto] items-center gap-x-ds-4 gap-y-ds-2 p-ds-4 text-left outline-none hover:bg-ds-surface focus-visible:ring-2 focus-visible:ring-ds-ring focus-visible:ring-inset md:grid-cols-[auto_minmax(0,2fr)_minmax(0,1.5fr)_minmax(0,1fr)_auto]"
			>
				<ChevronRight
					aria-hidden
					className={cn('h-4 w-4 text-ds-text-subtle transition-transform', isOpen && 'rotate-90')}
				/>
				<span className="flex min-w-0 flex-col">
					<code className="truncate font-ds-mono text-ds-base text-ds-text">{first.assetCode}</code>
					<span className="text-ds-xs text-ds-text-muted">
						Doc {first.documentId} · v{first.majorVersion}.{first.minorVersion}
					</span>
				</span>
				<span className="col-span-full row-start-2 md:col-span-1 md:row-start-auto">
					<FileStrip files={files} imported={imported.length} />
				</span>
				<time
					dateTime={first.createdAt}
					title={formatDateTime(first.createdAt)}
					className="hidden text-ds-sm text-ds-text-muted md:block"
				>
					{fromNow(first.createdAt)}
				</time>
				<span className="col-start-3 row-start-1 justify-self-end md:col-start-auto md:row-start-auto">
					<StatusBadge status={status} />
				</span>
			</button>

			{isOpen && (
				<section
					id={panelId}
					aria-label={`${first.assetCode} files`}
					className="border-ds-border border-t bg-ds-surface"
				>
					<div className="flex items-center justify-between gap-ds-2 px-ds-4 py-ds-3">
						<p className="text-ds-sm text-ds-text-muted">Started {formatDateTime(first.createdAt)}</p>
						<DownloadAll files={inLibrary} />
					</div>
					<ul className="flex flex-col divide-y divide-ds-border border-ds-border border-t">
						{files.map((f) => (
							<FileLine key={f.id} file={f} />
						))}
					</ul>
				</section>
			)}
		</li>
	)
}

/** Per-call `mutate` callbacks only fire for the latest call, so each download catches its own failure. */
const useSave = () => {
	const download = useVeevaVaultFileDownload()
	return (f: AssetFile, fileId: string) =>
		download.mutateAsync(fileId).catch((error) => notification.error(downloadErrorMessage(error, fileLabel(f.file))))
}

function DownloadAll({ files }: { files: { file: AssetFile; fileId: string }[] }) {
	const save = useSave()
	return (
		<Button
			size="sm"
			isDisabled={files.length === 0}
			onPress={() => void Promise.all(files.map(({ file, fileId }) => save(file, fileId)))}
		>
			<Download aria-hidden className="mr-ds-2 h-4 w-4" />
			Download all
		</Button>
	)
}

function FileLine({ file: f }: { file: AssetFile }) {
	const save = useSave()
	const { fileId } = f
	const deleted = f.status === VeevaVaultFileImportStatus.SUCCESS && fileId === null
	return (
		<li className="flex flex-wrap items-center gap-ds-3 px-ds-4 py-ds-3">
			<span
				className={cn(
					'flex h-10 w-10 shrink-0 items-center justify-center rounded-ds-md border',
					fileId !== null ? 'border-ds-success text-ds-success' : 'border-ds-border text-ds-text-subtle',
				)}
			>
				<FileGlyph format={f.format} />
			</span>
			<span className="flex min-w-0 flex-1 flex-col">
				<span className="text-ds-sm text-ds-text">
					{fileLabel(f.file)}
					{f.fileName && (
						<>
							<span aria-hidden className="text-ds-text-subtle">
								{' · '}
							</span>
							<span className="text-ds-text-muted">{f.fileName}</span>
						</>
					)}
				</span>
				<span className="flex flex-wrap items-center gap-x-ds-2 text-ds-xs text-ds-text-subtle">
					<code className="font-ds-mono">{f.file}</code>
					<span aria-hidden>·</span>
					<span>{formatLabel(f.format)}</span>
					{f.attempts > 0 && (
						<>
							<span aria-hidden>·</span>
							<span>
								{f.attempts} {f.attempts === 1 ? 'attempt' : 'attempts'}
							</span>
						</>
					)}
				</span>
				{f.lastError && <span className="text-ds-sm text-ds-danger-strong">{f.lastError}</span>}
				{deleted && <span className="text-ds-sm text-ds-text-muted">Deleted from the library</span>}
			</span>
			<StatusBadge status={f.status} />
			<Button
				variant="secondary"
				size="sm"
				aria-label={`Download ${fileLabel(f.file)}`}
				isDisabled={fileId === null}
				onPress={() => fileId && void save(f, fileId)}
			>
				Download
			</Button>
		</li>
	)
}
